
import React, { useEffect, useState, useRef } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import StorageService from "../../services/StorageService";
import VehicleReportService from "../../services/vehicleReportService";
import ProjectService from "../../services/ProjectService";
import VehicleRegistrationService from "../../services/vehicleRegistrationService";
import VehicleReadingService from "../../services/vehicleReadingService";
import utility from "../../utils/utility";

import ReactPaginate from "react-paginate";

export default function ViewVehicleReport() {
  const [reports, setReports] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const [loading, setLoading] = useState(false);

  const [projects, setProjects] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [readingDate, setReadingDate] = useState("");

  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const messageTimer = useRef(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date) ? "-" : date.toLocaleDateString("en-GB");
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d)) return "-";
    const date = d.toISOString().slice(0, 10);
    const time = d.toTimeString().slice(0, 5);
    return `${date} ${time}`;
  };

  const handleClearFilters = () => {
    setSelectedProjectId("");
    setSelectedVehicleId("");
    setReadingDate("");

    setVehicles([]);
    setExpandedRow(null);
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const fetchInitialReadings = async (pageNo = currentPage) => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany().companyId;
      const employeeId = StorageService.loginData().employeeId;
      const roleId = StorageService.loginData().roleId;
      const res = await VehicleReadingService.getByCompanyId(
        companyId,
        pageNo,
        itemsPerPage,
        "",
        "",
        "",
        "",
        employeeId,
        roleId
      );
      const data = res.data || [];
      setReports(data);
      setPageCount(res.pagination?.totalPages || 1);
      setCurrentPage(pageNo);
    } catch (error) {
      console.error(error);
      showMessage("Error fetching vehicle readings", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const closeMessage = () => {
    setMessage("");
    if (messageTimer.current) clearTimeout(messageTimer.current);
  };

  // 🔹 Load projects (same logic as AddVehicleReading)
  const loadProjects = async () => {
    try {
      const companyId = StorageService.activeCompany().companyId;
      const employeeId = StorageService.loginData().employeeId;
      const res = await ProjectService.getActiveProjectsByEmployee(
        companyId,
        employeeId
      );
      const data = res?.data || res || [];

      const mapped = data.map(p => ({
        id: p.id || p.projectId,
        projectName: p.projectName,
        projectCode: p.projectCode,
        categoryName: p.projectCategoryName || p.categoryName || p.category || ""
      }));

      setProjects(mapped);

    } catch {
      setProjects([]);
    }
  };

  // 🔹 Load vehicles when project changes
  const fetchVehicles = async (projectId) => {
    if (!projectId) {
      setVehicles([]);
      return;
    }
    try {
      const res = await VehicleRegistrationService.getByProjectId(projectId, true);
      setVehicles(res?.status ? res.data : res?.data || res || []);
    } catch {
      setVehicles([]);
    }
  };

  const handleProjectChange = (e) => {
    const value = e.target.value;
    setSelectedProjectId(value);
    setSelectedVehicleId("");
    fetchVehicles(value);
  };

  const handleSearch = async (pageNo = currentPage) => {
    if (!selectedProjectId && !selectedVehicleId && !readingDate) {
      showMessage("Please select at least one filter");
      return;
    }

    setLoading(true);
    try {
      const companyId = StorageService.activeCompany().companyId;

      const res = await VehicleReportService.getSearch({
        companyId,
        projectId: selectedProjectId || null,
        vehicleId: selectedVehicleId || null,
        readingDate: readingDate || null,
        page: pageNo,
        limit: itemsPerPage
      });

      const data = res.data || [];
      setPageCount(res.pagination?.totalPages || 1);
      setReports(data);
      setExpandedRow(null);
      setCurrentPage(pageNo);

    } catch (error) {
      console.error(error);
      showMessage("Error fetching vehicle report", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (row) => {
    setExpandedRow(expandedRow === row ? null : row);
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    if (selectedProjectId || selectedVehicleId || readingDate) {
      handleSearch(newPage);
    } else {
      fetchInitialReadings(newPage);
    }
  };

  const handleSearchClick = () => {
    handleSearch(1);
  };

  useEffect(() => {
    if (
      selectedProjectId === "" &&
      selectedVehicleId === "" &&
      readingDate === ""
    ) {
      fetchInitialReadings(1); // important (not handleSearch)
    }
  }, [selectedProjectId, selectedVehicleId, readingDate]);


  // useEffect(() => {
  //   loadReport();
  // }, [currentPage]);

  return (
    <>
      <Breadcrumb pageTitle="Vehicle Report" secondTitle="Vehicle Management" />

      {message && (
        <div
          className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"
            } d-flex justify-content-between align-items-center mb-2 p-2`}
        >
          <span>{message}</span>
          <button type="button" className="btn-close" onClick={closeMessage}></button>
        </div>
      )}

      <div className="row">
        <div className="card py-3 bg-white shadow-sm">

          {/* 🔹 Filters Section */}
          <div className="row mb-3">

            {/* Reading Date */}
            <div className="col-md-3 mb-2">
              <label className="form-label">Reading Date</label>
              <input
                type="date"
                className="form-control"
                value={readingDate}
                onChange={(e) => setReadingDate(e.target.value)}
              />
            </div>

            {/* Project Dropdown */}
            <div className="col-md-4 mb-2">
              <label className="form-label">Project</label>
              <select
                className="form-select"
                value={selectedProjectId}
                onChange={handleProjectChange}
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.projectName}-{p.projectCode}
                  </option>
                ))}
              </select>
            </div>

            {/* Vehicle Dropdown (Scrollable after 4–5 items) */}
            <div className="col-md-5 mb-2">
              <label className="form-label">Vehicle</label>
              <select
                className="form-select vehicle-scroll-select"
                value={selectedVehicleId}
                onChange={(e) => setSelectedVehicleId(e.target.value)}
              >
                <option value="">Select Vehicle</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    ({v.vehicleType}) --- {v.vehicleName}-{v.registrationNo}
                  </option>
                ))}
              </select>

              {selectedProjectId && vehicles.length === 0 && (
                <p className="text-danger mt-1" style={{ fontSize: "14px" }}>
                  No assigned vehicles for this project
                </p>
              )}
            </div>

          </div>

          {/* 🔹 Search Button */}

          <div className="mb-3 d-flex justify-content-end gap-2">


            <button
              className="btn btn-secondary"
              onClick={handleClearFilters}
            // disabled={!selectedProjectId && !selectedVehicleId && !readingDate}
            >
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>

            <button
              className="btn btn-primary"
              onClick={handleSearchClick}
              disabled={loading}
            >
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>

          </div>


          {/* 🔹 Report Table */}
          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Reading Date</th>
                  <th>Project</th>
                  <th>Vehicle</th>
                  <th>Reading Type</th>
                  <th>Total Readings</th>
                  <th>View</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="spinner-border text-primary"></div>
                    </td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No vehicle report found
                    </td>
                  </tr>
                ) : (
                  reports.map((r, index) => (
                    <React.Fragment key={index}>
                      <tr>
                        <td>{index + 1}</td>
                        <td>{formatDate(r.readingDate)}</td>
                        <td>{r.projectName ? (() => {
                          const cat = r.projectCategoryName || r.categoryName || r.category || (projects.find(p => String(p.id || p.projectId) === String(r.projectId || selectedProjectId) || (r.projectName && String(p.projectName).trim().toLowerCase() === String(r.projectName).trim().toLowerCase()))?.categoryName) || "";
                          const init = String(cat).trim().charAt(0).toUpperCase();
                          return init ? `${init}*${r.projectName}` : r.projectName;
                        })() : "-"}</td>
                        <td>{r.vehicleName || "-"}</td>
                        <td>{r.readingType || "-"}</td>
                        <td>{r.readings?.length || 0}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => toggleExpand(r)}
                          >
                            {expandedRow === r ? "Hide" : "View"}
                          </button>
                        </td>
                      </tr>

                      {/* 🔹 Expanded Readings */}
                      {expandedRow === r && (
                        <tr>
                          <td colSpan="7">
                            <div className="p-3 bg-light rounded">
                              <h6 className="mb-3">Reading Details</h6>

                              <div className="table-responsive">
                                <table className="table table-bordered table-striped mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>#</th>
                                      <th>From Reading</th>
                                      <th>To Reading</th>
                                      <th>Total Reading</th>
                                      <th>Start Date/Time</th>
                                      <th>End Date/Time</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.readings.map((rd, idx) => (
                                      <tr key={rd.id}>
                                        <td>{idx + 1}</td>
                                        <td>{rd.fromReading ?? "-"}</td>
                                        <td>{rd.toReading ?? "-"}</td>
                                        <td>
                                          {rd.toReading != null && rd.fromReading != null && rd.toReading !== "" && rd.fromReading !== ""
                                            ? +(Number(rd.toReading) - Number(rd.fromReading)).toFixed(2)
                                            : "-"}
                                        </td>
                                        <td>{utility.formatDbDateTime(rd.startTime)}</td>
                                        <td>{utility.formatDbDateTime(rd.endTime)}</td>
                                        <td>
                                          <span
                                            className={`badge ${rd.status?.toLowerCase() === "active"
                                              ? "bg-success"
                                              : "bg-secondary"
                                              }`}
                                          >
                                            {rd.status || "-"}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={pageCount}
              previousLabel="< Previous"
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
              forcePage={currentPage - 1}
            />


          </div>

        </div>
      </div>

      {/* 🔹 Dropdown Scroll Styling */}
      <style>{`
        .vehicle-scroll-select {
          max-height: 180px; /* ~4–5 options visible */
          overflow-y: auto;
        }
      `}</style>
    </>
  );
}